import type {
  OnboardingAttemptWire,
  OnboardingStatusWire,
  OnboardingStepWire,
  RemoteHealthWire,
} from './types';

/**
 * Traduz tentativa + saúde remota nas frases da tela (spec 2026-09-29 §7 «Catálogo de
 * problemas»; pranchas W2-A/W2-B). Função pura, um caso por linha do catálogo, para que a
 * frase de cada erro real seja testável sem montar componente — o mesmo motivo do
 * `channel-diagnosis.ts`.
 *
 * Regra de ouro das frases: dizer **de quem é o próximo passo**, e o botão ser esse passo.
 */

export type Tone = 'ok' | 'warn' | 'bad' | 'mute' | 'busy';

export type AcaoKind =
  | 'retomar'
  | 'reabrir'
  | 'informar-pin'
  | 'tentar-de-novo'
  | 'outro-caminho'
  | 'reconectar'
  | 'religar'
  | 'verificar-de-novo'
  | 'abrir-meta';

export interface Acao {
  kind: AcaoKind;
  label: string;
  href?: string;
}

export interface StepView {
  key: OnboardingStepWire;
  label: string;
  detail: string | null;
  state: 'done' | 'now' | 'todo' | 'err' | 'skip';
}

export interface Pendencia {
  who: 'Seu passo · na Meta' | 'Passo da Meta · em análise' | 'Seu passo · opcional';
  title: string;
  why: string;
  action: Acao | null;
  /** Mostra «Verificar de novo» ao lado — só onde o gesto do tenant muda algo que relemos. */
  verificar: boolean;
}

export interface Frase {
  tone: Tone;
  label: string;
  sentence: string | null;
  action: Acao | null;
}

export const EM_ANDAMENTO: readonly OnboardingStatusWire[] = [
  'exchanging',
  'subscribing',
  'registering',
  'syncing',
];

const WA_MANAGER = 'https://business.facebook.com/wa/manage/home/';
const BUSINESS_SUPPORT = 'https://business.facebook.com/business-support-home/';
const BUSINESS_VERIFY = 'https://business.facebook.com/settings/security';

// `labelFeito`: o passo concluído não fica no gerúndio (revisão de 30/Set, menor 8).
const PASSOS: Record<OnboardingStepWire, { label: string; labelFeito?: string; detail: string }> = {
  exchange: {
    label: 'Conta autorizada',
    detail: 'A Meta confirmou que a Lume pode atender por este número.',
  },
  subscribe: {
    label: 'Recebimento ligado',
    detail: 'As mensagens dos clientes passam a chegar aqui. Conferido, não só pedido.',
  },
  register: {
    label: 'Registrando o número',
    labelFeito: 'Número registrado',
    detail: 'A Lume cria e guarda o PIN de segurança por você.',
  },
  sync: {
    label: 'Trazendo contatos e conversas',
    labelFeito: 'Contatos e conversas trazidos',
    detail: 'Pode levar até um dia; você já pode atender enquanto isso.',
  },
  health: {
    label: 'Situação na Meta',
    detail: 'Forma de pagamento, nome de exibição, limite de envio.',
  },
  test: {
    label: 'Teste de conexão',
    detail: 'Perguntamos à Meta quem é este número e comparamos.',
  },
};

const ORDEM: Record<OnboardingAttemptWire['path'], OnboardingStepWire[]> = {
  dedicated: ['exchange', 'subscribe', 'register', 'health', 'test'],
  migration: ['exchange', 'subscribe', 'register', 'health', 'test'],
  business_app: ['exchange', 'subscribe', 'register', 'sync', 'health', 'test'],
};

export function stepsFor(a: OnboardingAttemptWire): StepView[] {
  return ORDEM[a.path].map((key) => {
    const base = PASSOS[key];
    if (a.path === 'business_app' && key === 'register') {
      return {
        key,
        label: 'Registro do número — não se aplica',
        detail: 'Neste caminho o celular continua sendo o dono do número.',
        state: 'skip',
      };
    }
    let state: StepView['state'] = 'todo';
    if (a.stepsDone.includes(key)) state = 'done';
    else if (a.currentStep === key) state = a.status === 'failed' ? 'err' : 'now';
    const label = state === 'done' ? (base.labelFeito ?? base.label) : base.label;
    return { key, label, detail: base.detail, state };
  });
}

/**
 * Os `current_step` do popup (spec §7 #1), pelas fontes B.1 de 29/Set. Se a Meta mandar outro
 * nome, cai no genérico «onde você estava» — nunca quebra.
 */
const PASSO_DA_META: Record<string, string> = {
  BUSINESS_ACCOUNT_SELECTION: 'escolher a conta da empresa',
  WABA_SELECTION: 'escolher a conta do WhatsApp',
  PHONE_NUMBER_SETUP: 'informar o número',
  PHONE_NUMBER_VERIFICATION: 'confirmar o número',
  BUSINESS_INFO: 'preencher os dados da empresa',
  PERMISSIONS: 'autorizar a Lume',
};

const RETOMAR: Acao = { kind: 'retomar', label: 'Retomar' };
const REABRIR: Acao = { kind: 'reabrir', label: 'Reabrir' };
const TENTAR: Acao = { kind: 'tentar-de-novo', label: 'Tentar de novo' };
const OUTRO: Acao = { kind: 'outro-caminho', label: 'Escolher outro caminho' };
const VERIFICAR_EMPRESA: Acao = {
  kind: 'abrir-meta',
  label: 'Verificar a empresa ↗',
  href: BUSINESS_VERIFY,
};
const SUPORTE_META: Acao = {
  kind: 'abrir-meta',
  label: 'Abrir o Business Support ↗',
  href: BUSINESS_SUPPORT,
};

type FraseFixa = Frase & { sentence: string };

/** Catálogo §7 por código. Chave é string: a Graph manda número, os nossos são string. */
const POR_CODIGO: Record<string, FraseFixa> = {
  // ── popup ──
  '3441049': {
    tone: 'bad',
    label: 'Número em uso',
    sentence:
      'Esse número está ligado a outra conta do WhatsApp Business. **Desconecte-o lá** (no sistema antigo ou no app do celular) e tente de novo em 15 minutos.',
    action: TENTAR,
  },
  '2655093': {
    tone: 'bad',
    label: 'Número em uso',
    sentence:
      'Esse número está ligado a outra conta do WhatsApp Business. **Desconecte-o lá** e tente de novo em 15 minutos.',
    action: TENTAR,
  },
  '2655094': {
    tone: 'bad',
    label: 'Número em uso',
    sentence:
      'Esse número já está registrado em outro sistema. **Desligue a verificação em duas etapas lá** e tente de novo.',
    action: TENTAR,
  },
  '3441030': {
    tone: 'bad',
    label: 'Número em uso',
    sentence: 'Esse número já pertence a outra conta. **Libere-o lá** antes de conectar aqui.',
    action: TENTAR,
  },
  '3441042': {
    tone: 'bad',
    label: 'Erro na Meta',
    sentence:
      'A Meta ainda não liberou esse recurso para números do seu país. Use o caminho **número novo ou dedicado**.',
    action: OUTRO,
  },
  '3441045': {
    tone: 'warn',
    label: 'Sem atividade',
    sentence:
      'A Meta acha que esse número foi pouco usado no app. **Use o WhatsApp Business por mais alguns dias** e tente de novo.',
    action: TENTAR,
  },
  '4563015': {
    tone: 'warn',
    label: 'App desatualizado',
    sentence: '**Atualize o WhatsApp Business no celular** e tente de novo.',
    action: TENTAR,
  },
  '2388098': {
    tone: 'bad',
    label: 'Limite da conta',
    sentence:
      'Sua conta na Meta chegou ao limite de contas do WhatsApp. **Verifique a empresa** para subir o limite.',
    action: VERIFICAR_EMPRESA,
  },
  '2494067': {
    tone: 'bad',
    label: 'Limite da conta',
    sentence:
      'Sua conta na Meta chegou ao limite de números. **Verifique a empresa** para subir o limite.',
    action: VERIFICAR_EMPRESA,
  },
  '1690114': {
    tone: 'bad',
    label: 'Limite da conta',
    sentence:
      'Sua conta na Meta chegou ao limite de portfólios. **Verifique a empresa** para subir o limite.',
    action: VERIFICAR_EMPRESA,
  },
  '2859009': {
    tone: 'bad',
    label: 'Conta restrita',
    sentence: 'Sua conta na Meta está restrita. Resolva no **Business Support Home** e volte aqui.',
    action: SUPORTE_META,
  },
  '4612001': {
    tone: 'bad',
    label: 'Conta restrita',
    sentence:
      'A Meta exige verificação em duas etapas na sua conta pessoal. Ative-a e tente de novo.',
    action: TENTAR,
  },
  '1357053': {
    tone: 'bad',
    label: 'Conta restrita',
    sentence:
      'Sua conta na Meta está com uma pendência. Resolva no **Business Support Home** e volte aqui.',
    action: SUPORTE_META,
  },
  // ── pós-popup ──
  '100': {
    tone: 'warn',
    label: 'O código venceu',
    sentence:
      'A Meta dá 30 segundos para concluirmos depois que a janela fecha, e passou. Reabra: **o número já está confirmado**, você não repete a verificação.',
    action: REABRIR,
  },
  '190': {
    tone: 'bad',
    label: 'Perdemos o acesso',
    sentence: 'A Meta não aceita mais a autorização que a Lume tinha. Reconectar leva um minuto.',
    action: REABRIR,
  },
  '133005': {
    tone: 'bad',
    label: 'Parou no registro',
    sentence:
      'Esse número **já tem um PIN de verificação** em duas etapas, criado fora da Lume. Digite-o para continuarmos — ou, se não lembra, troque-o no app do WhatsApp Business e volte aqui.',
    action: { kind: 'informar-pin', label: 'Informar o PIN' },
  },
  '133006': {
    tone: 'warn',
    label: 'Parou no registro',
    sentence: 'A verificação do número não terminou na Meta. Reabra a janela para concluir.',
    action: REABRIR,
  },
  '133016': {
    tone: 'bad',
    label: 'Muitas tentativas',
    sentence:
      'A Meta limita o registro a 10 tentativas em 72 horas, e o limite foi atingido. Tente de novo em algumas horas.',
    action: null,
  },
  '131031': {
    tone: 'bad',
    label: 'Conta restrita',
    sentence: 'A Meta restringiu esta conta. Veja o motivo no **Business Support Home**.',
    action: { kind: 'abrir-meta', label: 'Ver na Meta ↗', href: BUSINESS_SUPPORT },
  },
  'subscription-not-confirmed': {
    tone: 'warn',
    label: 'Ligando o recebimento…',
    sentence:
      'A Meta disse «feito» mas ainda não confirma que as mensagens virão para cá. Este passo é **nosso**, não seu: tente de novo; se não confirmar, a Lume é avisada.',
    action: TENTAR,
  },
  'missing-asset-ids': {
    tone: 'warn',
    label: 'Faltou um dado da Meta',
    sentence:
      'A janela fechou sem devolver os dados do número. Reabra: **o número já está confirmado**, você não repete a verificação.',
    action: REABRIR,
  },
};

function codigo(a: OnboardingAttemptWire): string | null {
  const c = a.lastError?.code;
  return c === null || c === undefined ? null : String(c);
}

/**
 * Polling só enquanto há o que esperar: popup aberto (`opened` SEM `metaEvent`) ou pipeline
 * andando. CANCEL/ERROR deixam o status `opened` no Engine — sem esta distinção o front pollaria
 * por até 14 dias uma tentativa que já parou (achado do revisor, 30/Set).
 */
export function deveContinuarPolling(a: OnboardingAttemptWire | null): boolean {
  if (a === null) return false;
  if (a.status === 'opened') return a.metaEvent === null;
  return EM_ANDAMENTO.includes(a.status);
}

export function describeAttempt(a: OnboardingAttemptWire): Frase {
  // ⚠️ O Engine NÃO muda o status em CANCEL/ERROR do popup: fica `opened` com `metaEvent`
  // preenchido (`recordPopupResult` só grava `popup_closed` no sucesso). Por isso o abandono e
  // o erro são lidos ANTES do ramo «janela aberta» — senão a tela diria que a janela está aberta
  // para sempre, sem botão.
  if (a.metaEvent === 'CANCEL' && (a.status === 'opened' || a.status === 'popup_closed')) {
    // Passo que não reconhecemos (ou que a Meta não mandou) não vira lugar inventado — antes a
    // frase dizia «Você parou em **onde você estava**» (revisão de 30/Set, menor 5).
    const onde = a.metaCurrentStep !== null ? PASSO_DA_META[a.metaCurrentStep] : undefined;
    return {
      tone: 'warn',
      label: 'Não terminou',
      sentence:
        onde !== undefined
          ? `Você parou em **${onde}**. Nada foi perdido: ao retomar, a Meta volta para onde você estava.`
          : 'A janela da Meta fechou antes do fim. Nada foi perdido: ao retomar, a Meta volta para onde você estava.',
      action: RETOMAR,
    };
  }
  if (a.status === 'opened' && a.metaEvent === null) {
    return {
      tone: 'busy',
      label: 'Conectando na Meta…',
      sentence:
        'A janela da Meta está aberta. Termine os passos lá — escolher a conta, confirmar o número, autorizar a Lume. Quando você fechar a janela na última tela, voltamos aqui automaticamente.',
      action: null,
    };
  }
  if (EM_ANDAMENTO.includes(a.status)) {
    return {
      tone: 'busy',
      label: a.status === 'syncing' ? 'Trazendo suas conversas…' : 'Ligando…',
      sentence: null,
      action: null,
    };
  }
  if (a.status === 'expired') {
    const prazo =
      a.path === 'business_app' ? '24 horas para sincronizar' : '14 dias para terminar a conexão';
    return {
      tone: 'warn',
      label: 'O prazo venceu',
      sentence: `A Meta dá ${prazo} depois da janela, e passou. **Conectar de novo** abre a janela outra vez.`,
      action: REABRIR,
    };
  }
  if (a.status === 'canceled') {
    return { tone: 'mute', label: 'Cancelada', sentence: null, action: null };
  }
  const code = codigo(a);
  // `100` na Graph é «parâmetro inválido» genérico; só vale como «código venceu» na troca.
  const fixa =
    code === null || (code === '100' && a.currentStep !== 'exchange')
      ? undefined
      : POR_CODIGO[code];
  if (fixa !== undefined) {
    const protocolo =
      a.metaSessionId === null ? '' : ` Protocolo para suporte: ${a.metaSessionId}.`;
    return { ...fixa, sentence: fixa.sentence + protocolo };
  }
  if (a.status === 'failed' || a.metaEvent === 'ERROR') {
    const detalhe = code === null ? '' : ` (código ${code})`;
    // Sem `exchange` feito, `resume` não tem token e não faz nada: a saída é reabrir o popup.
    const naTroca = a.currentStep === 'exchange' || a.metaEvent === 'ERROR';
    return {
      tone: 'bad',
      label: 'Algo falhou',
      sentence: `A Meta devolveu um erro que não reconhecemos${detalhe}. ${naTroca ? 'Reabra a janela' : 'Tente de novo'}; se repetir, a Lume já foi avisada.`,
      action: naTroca ? REABRIR : TENTAR,
    };
  }
  if (a.status === 'pending_customer') {
    return { tone: 'warn', label: 'Recebe · falta passo seu', sentence: null, action: null };
  }
  return { tone: 'ok', label: 'Conectado', sentence: null, action: null };
}

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * O limite diário da Meta em português: `TIER_250` → «250», `TIER_2K` → «2.000»; sem limite →
 * `'sem-limite'`; ausente → «250» (o piso de número novo); formato que não reconhecemos → `null`
 * (a frase fala do limite sem número). Antes o texto cru vazava: «UNLIMITED clientes por dia»
 * (revisão de 30/Set, menor 4).
 */
export function limiteDiario(messagingLimit: string | null): string | null {
  if (messagingLimit === null) return '250';
  const m = messagingLimit.toUpperCase().replace(/^TIER_/, '');
  if (m === 'UNLIMITED') return 'sem-limite';
  const n = /^(\d+)(K?)$/.exec(m);
  if (n === null) return null;
  return (Number(n[1]) * (n[2] === 'K' ? 1000 : 1)).toLocaleString('pt-BR');
}

export function describeRemote(
  r: RemoteHealthWire,
  displayName: string | null,
): {
  pendencias: Pendencia[];
  degraded: { label: string; sentence: string; action: Acao | null } | null;
} {
  const pendencias: Pendencia[] = [];
  // Só o 141006 diz «sem pagamento». BLOCKED sem motivo informado não é afirmado como
  // pagamento — manda ver na Meta (revisão de 30/Set, menor 6).
  const semPagamento = r.blockers.some((b) => b.code === '141006');
  if (!semPagamento && r.canSendMessage === 'BLOCKED' && r.blockers.length === 0) {
    pendencias.push({
      who: 'Seu passo · na Meta',
      title: 'A Meta bloqueou o envio',
      why: 'A Meta bloqueou as conversas iniciadas pela empresa e não nos disse o motivo. O WhatsApp Manager mostra o que falta. As respostas aos clientes que escrevem continuam funcionando.',
      action: { kind: 'abrir-meta', label: 'Ver no WhatsApp Manager ↗', href: WA_MANAGER },
      verificar: true,
    });
  }
  if (semPagamento) {
    pendencias.push({
      who: 'Seu passo · na Meta',
      title: 'Adicionar uma forma de pagamento',
      why: 'A Meta cobra por conversa iniciada pela empresa, e sem cartão ela bloqueia esse tipo de envio. Leva 2 minutos no WhatsApp Manager. Não há cobrança da Lume nesse passo.',
      action: { kind: 'abrir-meta', label: 'Abrir o WhatsApp Manager ↗', href: WA_MANAGER },
      verificar: true,
    });
  }
  const nome = displayName ?? 'seu nome de exibição';
  if (r.nameStatus === 'PENDING_REVIEW' || r.nameStatus === 'NONE') {
    pendencias.push({
      who: 'Passo da Meta · em análise',
      title: `Nome de exibição «${nome}» em análise`,
      why: 'A Meta confere o nome antes de mostrá-lo aos clientes. Costuma levar de minutos a 2 dias. Enquanto isso, o cliente vê só o número.',
      action: null,
      verificar: false,
    });
  } else if (r.nameStatus === 'DECLINED') {
    pendencias.push({
      who: 'Seu passo · na Meta',
      title: `Nome de exibição «${nome}» recusado`,
      why: 'A Meta não aprovou o nome. Edite-o no WhatsApp Manager seguindo as regras de nome de exibição.',
      action: { kind: 'abrir-meta', label: 'Editar no WhatsApp Manager ↗', href: WA_MANAGER },
      verificar: true,
    });
  }
  const limite = limiteDiario(r.messagingLimit);
  if (
    r.businessVerification !== null &&
    r.businessVerification.toLowerCase() !== 'verified' &&
    limite !== 'sem-limite'
  ) {
    pendencias.push({
      who: 'Seu passo · opcional',
      title: 'Verificar a empresa para falar com mais gente',
      why: `${
        limite === null
          ? 'Hoje há um limite diário de conversas iniciadas pela empresa.'
          : `Hoje você pode iniciar conversa com ${limite} clientes por dia.`
      } Com a empresa verificada na Meta o limite sobe e cresce com o uso. Precisa de CNPJ e um documento.`,
      action: VERIFICAR_EMPRESA,
      verificar: false,
    });
  }

  let degraded: { label: string; sentence: string; action: Acao | null } | null = null;
  const reason = r.degradedReason ?? (r.subscribed === false ? 'unsubscribed' : null);
  switch (reason) {
    case 'token_revoked':
      degraded = {
        label: 'Perdemos o acesso',
        sentence:
          'Alguém **removeu a Lume** da conta da empresa na Meta. Desde então nenhuma mensagem de cliente chega aqui. Reconectar leva um minuto e não pede verificação do número de novo.',
        action: { kind: 'reconectar', label: 'Reconectar' },
      };
      break;
    case 'unsubscribed':
      degraded = {
        label: 'Recebimento parou',
        sentence:
          'A Meta deixou de mandar as mensagens para cá — em geral porque outro sistema foi ligado ao mesmo número, ou alguém mexeu no painel da Meta. Seu acesso continua válido; só o encaminhamento caiu. Este gesto é nosso: um clique.',
        action: { kind: 'religar', label: 'Religar recebimento' },
      };
      break;
    case 'deregistered':
      degraded = {
        label: 'Número desregistrado',
        sentence:
          'O número saiu do registro da API na Meta. Reconectar registra de novo com o PIN guardado.',
        action: { kind: 'reconectar', label: 'Reconectar' },
      };
      break;
    case 'restricted': {
      const rs = r.restrictions[0];
      const ate =
        typeof rs?.expiresAt === 'string' && rs.expiresAt !== ''
          ? ` até ${dataCurta(rs.expiresAt)}`
          : '';
      const rem =
        typeof rs?.remediation === 'string' && rs.remediation !== ''
          ? ` O que a Meta pede: ${rs.remediation}.`
          : '';
      degraded = {
        label: 'Restrição da Meta',
        sentence: `A Meta restringiu **o envio de mensagens iniciadas pela empresa${ate}**. Responder a quem escreve continua liberado.${rem}`,
        action: { kind: 'abrir-meta', label: 'Ver na Meta ↗', href: BUSINESS_SUPPORT },
      };
      break;
    }
    case 'banned':
      degraded = {
        label: 'Conta desativada',
        sentence:
          'A Meta desativou esta conta do WhatsApp Business. Recorra no **Business Support Home**.',
        action: { kind: 'abrir-meta', label: 'Recorrer na Meta ↗', href: BUSINESS_SUPPORT },
      };
      break;
    case 'offboarded':
      degraded = {
        label: 'Conexão caiu',
        sentence:
          'A Meta encerrou a conexão do celular com a API (troca de aparelho ou inatividade). **Reconectar** refaz o vínculo.',
        action: { kind: 'reconectar', label: 'Reconectar' },
      };
      break;
    default:
      degraded = null;
  }
  return { pendencias, degraded };
}
